import type { ChronicleResponse } from '@brimstone/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface ChronicleFilters {
  characterIds: string[];
  types: string[];
  /** A week id ("2026-11-18"), or null for all weeks. */
  week: string | null;
}

function query(f: ChronicleFilters, limit: number, cursor: string | null): string {
  const p = new URLSearchParams({ limit: String(limit) });
  if (f.characterIds.length) p.set('characterId', f.characterIds.join(','));
  if (f.types.length) p.set('type', f.types.join(','));
  if (f.week) p.set('week', f.week);
  if (cursor) p.set('cursor', cursor);
  return `/api/chronicle?${p.toString()}`;
}

const PAGE = 50;

/** The feed, a page at a time (SPEC §6.6). */
export function useChronicle(f: ChronicleFilters) {
  return useInfiniteQuery({
    queryKey: ['chronicle', f],
    queryFn: ({ pageParam }) => api<ChronicleResponse>(query(f, PAGE, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** The latest entries of one character, for its Book page. */
export function useCharacterChronicle(characterId: string, limit = 8) {
  return useQuery({
    queryKey: ['chronicle', 'character', characterId, limit],
    queryFn: () =>
      api<ChronicleResponse>(
        query({ characterIds: [characterId], types: [], week: null }, limit, null),
      ),
  });
}
